import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams, useHistory, Link} from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Icon,
  Image,
  Label,
  List,
  Loader,
  Message,
  Modal,
  Segment,
} from 'semantic-ui-react';
import InviteReader from '../readerIndex/inviteReader';
import {
  createSample,
  updateSample,
  getSample,
  generateAudio,
  getStats,
  listVoices,
  listSourceBooks,
  getSourceChapter,
} from './api';

const Page = styled.div`
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  margin: 12px 0;
`;

const StatBox = styled(Segment)`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(34, 36, 38, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const ContactDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const Muted = styled.div`
  color: var(--br-muted);
  font-size: 0.9em;
  word-break: break-word;
`;

const Quote = styled.em`
  display: block;
  margin-top: 4px;
`;

const SampleChapterEditor = () => {
  const idToken = useSelector((s) => s.user.idToken);
  const {id} = useParams();
  const history = useHistory();
  const isNative = Boolean(window.ReactNativeWebView);
  const [form, setForm] = useState({
    title: '',
    variantLabel: 'A',
    description: '',
    genres: [],
    language: 'en',
    contentText: '',
    listed: false,
  });
  const [sample, setSample] = useState(null);
  const [stats, setStats] = useState(null);
  const [voicesInfo, setVoicesInfo] = useState({configured: false, voices: []});
  const [voiceId, setVoiceId] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sourceBooks, setSourceBooks] = useState(null);
  const [pickedBookId, setPickedBookId] = useState(null);
  const [pickedVersionId, setPickedVersionId] = useState(null);
  // The reader currently being invited to a book, or null when the modal is
  // closed. Holds the `{_id, displayName}` shape InviteReader expects.
  const [invitee, setInvitee] = useState(null);
  const [pickedChapterId, setPickedChapterId] = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);

  const isNew = !id;

  const load = async () => {
    if (!id) return;
    try {
      const s = await getSample(idToken, id);
      setSample(s);
      setForm({
        title: s.title || '',
        variantLabel: s.variantLabel || 'A',
        description: s.description || '',
        genres: s.genres || [],
        language: s.language || 'en',
        contentText: s.contentText || '',
        listed: !!s.listed,
      });
      setVoiceId(s.audioVoiceId);
      const st = await getStats(idToken, id);
      setStats(st);
    } catch (e) {
      setError(e.message);
    }
  };

  const loadVoices = async () => {
    try {
      const v = await listVoices(idToken);
      setVoicesInfo(v);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => {
    if (idToken) {
      load();
      loadVoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, id]);

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        const created = await createSample(idToken, form);
        history.replace(`/sample-chapters/edit/${created._id}`);
      } else {
        const updated = await updateSample(idToken, id, form);
        setSample(updated);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setError(null);
    if (!sourceBooks) {
      setPickerLoading(true);
      try {
        const books = await listSourceBooks(idToken);
        setSourceBooks(books);
      } catch (e) {
        setError(e.message);
      } finally {
        setPickerLoading(false);
      }
    }
  };

  const handleCopyChapter = async () => {
    if (!pickedBookId || !pickedChapterId) return;
    setPickerLoading(true);
    setError(null);
    try {
      const chapter = await getSourceChapter(
        idToken,
        pickedBookId,
        pickedChapterId
      );
      setForm((prev) => ({
        ...prev,
        title: prev.title || chapter.title || '',
        contentText: chapter.contentText || '',
      }));
      setPickerOpen(false);
      setPickedBookId(null);
      setPickedVersionId(null);
      setPickedChapterId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setPickerLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!sample) return;
    setBusy(true);
    setError(null);
    try {
      // Note: in production a Paddle one-off purchase flow runs first;
      // here we send a placeholder paymentToken and rely on the server's
      // BR_TTS_PAYMENT_BYPASS flag for local dev.
      await generateAudio(idToken, sample._id, {
        voiceId,
        paymentToken: 'dev-placeholder',
      });
      // poll for status
      const interval = setInterval(async () => {
        try {
          const updated = await getSample(idToken, sample._id);
          setSample(updated);
          if (updated.ttsStatus === 'ready' || updated.ttsStatus === 'failed') {
            clearInterval(interval);
            setBusy(false);
          }
        } catch (e) {
          clearInterval(interval);
          setBusy(false);
        }
      }, 3000);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (!isNew && !sample) return <Loader active />;

  const voiceOptions = voicesInfo.voices.map((v) => ({
    key: v.voice_id,
    value: v.voice_id,
    text: v.name,
  }));

  // The API splits raters in two and that split is the privacy model: readers
  // with a live opt-in come back identified under `contacts`, everyone else
  // comes back anonymised under `ratings` with no uid at all. Do not try to
  // re-derive one from the other here — the client is not the filter.
  const contacts = (stats && stats.contacts) || [];
  const anonymousRatings = (stats && stats.ratings) || [];

  return (
    <Page>
      <Button as={Link} to="/sample-chapters" basic size="small">
        <Icon name="arrow left" /> Back
      </Button>
      <h1>
        {isNew ? 'New sample chapter' : 'Edit sample chapter'}{' '}
        {!isNew && <Label>Variant {form.variantLabel}</Label>}
      </h1>
      {error && <Message error content={error} />}

      <Form>
        <Form.Field>
          <label>Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
          />
        </Form.Field>
        <Row>
          <Form.Field>
            <label>Variant label (e.g. A, B, "Action opening")</label>
            <input
              value={form.variantLabel}
              onChange={(e) => setForm({...form, variantLabel: e.target.value})}
            />
          </Form.Field>
          <Form.Field>
            <label>Language</label>
            <input
              value={form.language}
              onChange={(e) => setForm({...form, language: e.target.value})}
            />
          </Form.Field>
        </Row>
        <Form.Field>
          <label>Short description (shown in discover)</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({...form, description: e.target.value})}
          />
        </Form.Field>
        <Form.Field>
          <label>Chapter text</label>
          <div style={{marginBottom: 8}}>
            <Button type="button" basic size="small" onClick={openPicker}>
              <Icon name="copy" /> Copy from one of my books
            </Button>
          </div>
          <textarea
            rows={20}
            value={form.contentText}
            onChange={(e) => setForm({...form, contentText: e.target.value})}
          />
          <small>
            {(form.contentText || '').trim().split(/\s+/).filter(Boolean).length}{' '}
            words
          </small>
        </Form.Field>
        <Form.Field>
          <Checkbox
            label="List publicly so readers can find and rate this sample"
            checked={form.listed}
            onChange={(_, d) => setForm({...form, listed: !!d.checked})}
          />
        </Form.Field>

        <Button primary onClick={handleSave} disabled={busy}>
          <Icon name="save" /> {busy ? 'Saving…' : 'Save'}
        </Button>
      </Form>

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        size="small"
      >
        <Modal.Header>Copy a chapter from one of your books</Modal.Header>
        <Modal.Content>
          {pickerLoading && <Loader active inline="centered" />}
          {!pickerLoading && sourceBooks && sourceBooks.length === 0 && (
            <Message info>
              You don't have any books with chapters yet.
            </Message>
          )}
          {!pickerLoading && sourceBooks && sourceBooks.length > 0 && (() => {
            const pickedBook = sourceBooks.find((b) => b._id === pickedBookId);
            const versions = pickedBook ? pickedBook.versions || [] : [];
            const pickedVersion = versions.find((v) => v._id === pickedVersionId)
              || versions[0];
            const chapters = pickedVersion ? pickedVersion.chapters || [] : [];
            return (
              <Form>
                <Form.Field>
                  <label>Book</label>
                  <Dropdown
                    selection
                    fluid
                    placeholder="Pick a book"
                    value={pickedBookId}
                    onChange={(_, d) => {
                      const book = sourceBooks.find((b) => b._id === d.value);
                      setPickedBookId(d.value);
                      setPickedVersionId(book ? book.latestVersionId : null);
                      setPickedChapterId(null);
                    }}
                    options={sourceBooks.map((b) => ({
                      key: b._id,
                      value: b._id,
                      text: b.title || '(Untitled book)',
                    }))}
                  />
                </Form.Field>
                {pickedBook && versions.length > 1 && (
                  <Form.Field>
                    <label>Version</label>
                    <Dropdown
                      selection
                      fluid
                      placeholder="Pick a version"
                      value={pickedVersion ? pickedVersion._id : null}
                      onChange={(_, d) => {
                        setPickedVersionId(d.value);
                        setPickedChapterId(null);
                      }}
                      options={versions.map((v, i) => {
                        const isLatest = v._id === pickedBook.latestVersionId;
                        const label =
                          v.versionName ||
                          (v.versionNumber
                            ? `Version ${v.versionNumber}`
                            : `Version ${versions.length - i}`);
                        return {
                          key: v._id || `legacy-${i}`,
                          value: v._id,
                          text: `${label}${isLatest ? ' (latest)' : ''}`,
                        };
                      })}
                    />
                  </Form.Field>
                )}
                <Form.Field>
                  <label>Chapter</label>
                  <Dropdown
                    selection
                    fluid
                    placeholder="Pick a chapter"
                    value={pickedChapterId}
                    disabled={!pickedBookId || chapters.length === 0}
                    onChange={(_, d) => setPickedChapterId(d.value)}
                    options={chapters.map((c) => ({
                      key: c._id,
                      value: c._id,
                      text: `${c.title || '(Untitled chapter)'}${
                        c.wordCount ? ` — ${c.wordCount} words` : ''
                      }`,
                    }))}
                  />
                </Form.Field>
                <Message size="tiny">
                  The chapter's text will replace whatever you have in the
                  "Chapter text" field. The current title is kept unless empty.
                </Message>
              </Form>
            );
          })()}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setPickerOpen(false)} disabled={pickerLoading}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleCopyChapter}
            disabled={!pickedBookId || !pickedChapterId || pickerLoading}
          >
            Copy chapter
          </Button>
        </Modal.Actions>
      </Modal>

      {!isNew && (!isNative || (sample && sample.audioUrl)) && (
        <Segment>
          <h3>
            <Icon name="volume up" /> Audio (ElevenLabs TTS)
          </h3>
          {!voicesInfo.configured && (
            <Message
              warning
              content="ElevenLabs is not configured on this server. Set BR_ELEVENLABS_API_KEY to enable audio generation."
            />
          )}
          {!isNative && <p>
            Generate a high-quality narration of this sample.{' '}
            {sample && sample.audioUrl && (
              <Label color="green">
                <Icon name="check" /> Audio ready
              </Label>
            )}
            {sample && sample.ttsStatus === 'generating' && (
              <Label color="orange">
                <Icon name="hourglass half" /> Generating…
              </Label>
            )}
            {sample && sample.ttsStatus === 'failed' && (
              <Label color="red">
                <Icon name="warning" /> Failed: {sample.ttsError}
              </Label>
            )}
          </p>}
          {!isNative && <Row>
            <Dropdown
              placeholder="Pick a voice"
              selection
              options={voiceOptions}
              value={voiceId}
              onChange={(_, d) => setVoiceId(d.value)}
              disabled={!voicesInfo.configured || voiceOptions.length === 0}
            />
            <Button
              onClick={handleGenerate}
              disabled={
                busy ||
                !voicesInfo.configured ||
                !sample ||
                sample.ttsStatus === 'generating'
              }
            >
              {sample && sample.audioUrl ? 'Regenerate audio' : 'Generate audio'}
            </Button>
          </Row>}
          {sample && sample.audioUrl && (
            <audio controls src={sample.audioUrl} style={{width: '100%'}} />
          )}
        </Segment>
      )}

      {!isNew && stats && (
        <>
          <StatBox>
            <div>
              <strong>Ratings</strong>
              <div>{stats.sample.ratingsCount || 0}</div>
            </div>
            <div>
              <strong>Want to keep reading</strong>
              <div>
                {stats.sample.yesCount || 0} yes / {stats.sample.noCount || 0} no
              </div>
            </div>
            <div>
              <strong>Opted in for contact</strong>
              <div>{stats.sample.contactOptInCount || 0}</div>
            </div>
          </StatBox>

          {stats.siblings && stats.siblings.length > 0 && (
            <Segment>
              <h3>A/B variants</h3>
              {stats.siblings.map((sib) => (
                <div key={sib._id}>
                  <Label>Variant {sib.variantLabel}</Label> {sib.title} —{' '}
                  {sib.yesCount}/{sib.ratingsCount} yes (
                  {sib.contactOptInCount} contact opt-ins){' '}
                  <Link to={`/sample-chapters/edit/${sib._id}`}>open</Link>
                </div>
              ))}
            </Segment>
          )}

          <Segment>
            <h3>
              <Icon name="mail" /> Readers who opted in for contact
            </h3>
            <p>
              These readers said yes to "would you keep reading?" and consented
              to be contacted when more material is ready. They can revoke at
              any time — once they do, they disappear from this list.
            </p>
            {contacts.length === 0 && <p>No opt-ins yet.</p>}
            {contacts.map((c) => (
              <ContactRow key={c.ratingId}>
                {c.user.photoUrl && (
                  <Image src={c.user.photoUrl} avatar size="mini" />
                )}
                <ContactDetails>
                  <strong>{c.user.displayName}</strong>
                  <Muted>
                    <a href={`mailto:${c.user.email}`}>{c.user.email}</a>
                  </Muted>
                  {c.comment && <Quote>"{c.comment}"</Quote>}
                </ContactDetails>
                <Button
                  primary
                  size="small"
                  onClick={() =>
                    setInvitee({
                      _id: c.user._id,
                      displayName: c.user.displayName,
                    })
                  }
                >
                  <Icon name="paper plane" /> Invite as beta reader
                </Button>
              </ContactRow>
            ))}
          </Segment>

          {anonymousRatings.length > 0 && (
            <Segment>
              <h3>
                <Icon name="comments" /> Other feedback
              </h3>
              <p>
                Answers from readers who did not opt in to be contacted. Their
                comments are yours to read; who wrote them is not shared.
              </p>
              <List divided relaxed>
                {/* Keyed positionally: the API deliberately withholds each
                    row's id so a revoked reader cannot be re-linked to the
                    contact they used to appear as. */}
                {anonymousRatings.map((r, i) => (
                  <List.Item key={i}>
                    <List.Content>
                      <Label
                        size="tiny"
                        color={r.wantsToContinue ? 'green' : undefined}
                      >
                        {r.wantsToContinue ? 'Would keep reading' : 'Would not'}
                      </Label>
                      {r.comment && <Quote>"{r.comment}"</Quote>}
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            </Segment>
          )}
        </>
      )}

      {invitee && (
        <Modal open onClose={() => setInvitee(null)}>
          <Modal.Header>Invite {invitee.displayName}</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              {/* Same component the reader index uses, so book + version
                  selection, the reader cap and the custom message all behave
                  identically here. `source` tags the Reader so this funnel is
                  measurable. */}
              <InviteReader
                reader={invitee}
                source="sample_chapter_contact_opt_in"
                onClose={() => setInvitee(null)}
              />
            </Modal.Description>
          </Modal.Content>
        </Modal>
      )}
    </Page>
  );
};

export default SampleChapterEditor;
